import DuplicateContractModal from "../contract/DuplicateContractModal";
import { Copy, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "../../axios/AxiosInstance";
import { apiErrorMessage } from "../../utils/apiError";
import { contractDate } from "../../utils/planningDates";
import { candidateName } from "../../utils/candidateName";
import { getContractStatus } from "../../utils/contractStatus";
import { contractDocumentName, loadContractPdf, presentContractPdf } from "../../utils/contractDocument";
import { message } from "antd";

const frenchDate = (value) =>
  contractDate(value)?.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) || "—";
const paidRate = (value) =>
  value == null ||
  String(value).trim() === "" ||
  !Number.isFinite(Number(String(value).replace(",", ".")))
    ? "—"
    : new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(
        Number(String(value).replace(",", ".")),
      );

const CandidatContract = ({ candidate }) => {
  const [creating, setCreating] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    loadContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadContract = (contract) => {
    if (!contract?._id || downloadingId) return;
    setDownloadingId(contract._id);
    loadContractPdf(axios, contract)
      .then((blob) => presentContractPdf(blob, `${contractDocumentName(contract)}.pdf`, "download"))
      .catch((error) => message.error(apiErrorMessage(error, "Impossible de télécharger ce contrat")))
      .finally(() => setDownloadingId(null));
  };

  const loadContract = () => {
    if (candidate.pld == null || String(candidate.pld).trim() === "") return;
    axios
      .get(`pld-contract/candidate/${candidate.pld}`)
      .then((res) => {
        setContracts(Array.isArray(res.data) ? res.data : []);
      })
      .catch((e) => console.log(e));
  };

  const sortedContracts = [...contracts].sort((a, b) => {
    const first = String(a.NumeroContrat ?? "").trim();
    const second = String(b.NumeroContrat ?? "").trim();
    const firstNumeric = /^\d+$/.test(first);
    const secondNumeric = /^\d+$/.test(second);
    if (firstNumeric !== secondNumeric) return firstNumeric ? -1 : 1;
    return (
      second.localeCompare(first, "fr", { numeric: true }) ||
      String(b.NumeroAvenant ?? "").localeCompare(
        String(a.NumeroAvenant ?? ""),
        "fr",
        { numeric: true },
      )
    );
  });

  return (
    <div className="space-y-4 text-sm">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Contrats</h2>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-semibold text-white"
        >
          <Plus size={16} />
          Créer un contrat
        </button>
      </div>

      {contracts.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-6 text-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 text-gray-400 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="text-gray-600">Aucun contrat disponible</p>
          </div>
        </div>
      )}

      {contracts.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full bg-white">
            <thead className="text-xs font-medium text-gray-700 uppercase bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left">Contrat</th>
                <th className="px-4 py-2 text-left">Période</th>
                <th className="px-4 py-2 text-left">Fin de mission réelle</th>
                <th className="px-4 py-2 text-left">Client</th>
                <th className="px-4 py-2 text-left">Intérimaire</th>
                <th className="px-4 py-2 text-left">Poste</th>
                <th className="px-4 py-2 text-left">Taux payé</th>
                <th className="px-4 py-2 text-left">Statut</th>
                <th className="px-4 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm text-gray-600">
              {sortedContracts.map((contract, index) => (
                <tr key={index} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">{contractDocumentName(contract)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <p>Du {frenchDate(contract.DateDebut)}</p>
                    <p>Au {frenchDate(contract.DateFin)}</p>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {frenchDate(contract.DateFinMission)}
                  </td>
                  <td className="px-4 py-3">{contract.RaisonSociale || "—"}</td>
                  <td className="px-4 py-3">
                    {contract.NomPrenom ||
                      candidateName(candidate) ||
                      "Non assigné"}
                  </td>
                  <td className="px-4 py-3">
                    {contract.TxtQualification || contract.Qualification || "—"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {paidRate(contract.TauxPaye ?? contract.tauxHoraire)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium whitespace-nowrap">
                      {getContractStatus(contract)}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      title="Dupliquer le contrat"
                      aria-label={`Dupliquer le contrat ${contract.NumeroContrat}`}
                      onClick={() => setDuplicate(contract)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Copy size={18} />
                    </button>
                    <button
                      type="button"
                      className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-40"
                      title="Télécharger"
                      aria-label={`Télécharger le contrat ${contractDocumentName(contract)}`}
                      disabled={!contract._id || downloadingId === contract._id}
                      onClick={() => downloadContract(contract)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating && (
        <DuplicateContractModal
          candidate={candidate}
          onClose={() => setCreating(false)}
        />
      )}
      {duplicate && (
        <DuplicateContractModal
          key={duplicate._id || duplicate.NumeroContrat}
          source={duplicate}
          candidate={candidate}
          onClose={() => {
            setDuplicate(null);
            loadContract();
          }}
        />
      )}
    </div>
  );
};

export default CandidatContract;
